import React, { ReactNode, useContext, useEffect, useId, useState } from 'react';
import SvgRobot from '@icons/ai-robot.svg?react';
import SvgPlus from '@icons/plus-circle.svg?react';
import SvgLoading from '@icons/loading.svg?react';
import { ApiContext } from '@api/api.tsx';
import { FeaturesEnum, FindCarrierResponseOutDTO, FindCarrierTypeEnum } from '@api/ringil-api.v2.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import { Carrier } from '@components/RingilComponents/CarrierDistributionSelector/CarrierDistributionSelector.tsx';
import { isSameArray } from '@utils/utils.tsx';

export type AiCarrierAutoSelectionProps = {
  label: string | ReactNode; // e.g., t('ai.fillCarrier')
  className?: string;
  id?: string;
  aiCarrierAutoSelectionDto?: AiCarrierAutoSelectionDto,
  onSelect?: (values: Carrier[]) => void;
  selectedCarriers?: Carrier[];
  allowedCarrierTypes?: FindCarrierTypeEnum[];
  multiSelect?: boolean;
  dataTestId?: string;
};

export type AiCarrierAutoSelectionDto = {
  loadingLocationId?: string;
  unloadingLocationId?: string;
  loadingCity?: string;
  unloadingCity?: string;
  loadingCountry?: string;
  unloadingCountry?: string;
  weight?: number;
  countPallets?: number;
  distance?: number;
  cargoAmount?: number;
};

export const AiCarrierAutoSelection: React.FC<AiCarrierAutoSelectionProps> = ({
                                                                                label,
                                                                                className,
                                                                                id,
                                                                                aiCarrierAutoSelectionDto,
                                                                                onSelect,
                                                                                selectedCarriers = [],
                                                                                allowedCarrierTypes = [],
                                                                                multiSelect = true,
                                                                                dataTestId,
                                                                              }) => {

  const { tr } = useTranslationLgs();
  const autoId = `aicarrier_${useId().replace(/:/g, '-')}`;
  const rootId = id || autoId;
  const { aiCarrierAutoSelectionV3 } = useContext(ApiContext);
  const { hasFeature } = useContext(AuthenticatedUserContext);
  const [options, setOptions] = useState<FindCarrierResponseOutDTO[]>([]);
  const [filteredOptions, setFilteredOptions] = useState<FindCarrierResponseOutDTO[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const aiCarrierAutoSelectEnabled = hasFeature([FeaturesEnum.AI_CARRIER_AUTO_SELECTION]);
  const {
    loadingLocationId,
    unloadingLocationId,
    loadingCity,
    loadingCountry,
    unloadingCity,
    unloadingCountry,
    weight,
    countPallets,
    distance,
    cargoAmount,
  } = aiCarrierAutoSelectionDto || {};

  const [debouncedLoadingCity, setDebouncedLoadingCity] = useState(loadingCity);
  const [debouncedLoadingCountry, setDebouncedLoadingCountry] = useState(loadingCountry);
  const [debouncedUnloadingCity, setDebouncedUnloadingCity] = useState(unloadingCity);
  const [debouncedUnloadingCountry, setDebouncedUnloadingCountry] = useState(unloadingCountry);
  const [debouncedWeight, setDebouncedWeight] = useState(weight);
  const [debouncedCountPallets, setDebouncedCountPallets] = useState(countPallets);
  const [debouncedDistance, setDebouncedDistance] = useState(distance);
  const [debouncedCargoAmount, setDebouncedCargoAmount] = useState(cargoAmount);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedLoadingCity(loadingCity);
      setDebouncedLoadingCountry(loadingCountry);
      setDebouncedUnloadingCity(unloadingCity);
      setDebouncedUnloadingCountry(unloadingCountry);
      setDebouncedWeight(weight);
      setDebouncedCountPallets(countPallets);
      setDebouncedDistance(distance);
      setDebouncedCargoAmount(cargoAmount);
    }, 1000);

    return () => clearTimeout(handler);
  }, [loadingCity, loadingCountry, unloadingCity, unloadingCountry, weight, countPallets, distance, cargoAmount]);

  useEffect(() => {
    if (
      aiCarrierAutoSelectEnabled
      && (loadingLocationId || debouncedLoadingCity || debouncedLoadingCountry)
      && (unloadingLocationId || debouncedUnloadingCity || debouncedUnloadingCountry)
      && (
        (debouncedWeight !== undefined && debouncedWeight !== null)
        || (debouncedCountPallets !== undefined && debouncedCountPallets !== null)
        || (debouncedDistance !== undefined && debouncedDistance !== null)
        || (debouncedCargoAmount !== undefined && debouncedCargoAmount !== null)
      )
    ) {
      setLoading(true);
      aiCarrierAutoSelectionV3(
        {
          loadingLocationId,
          unloadingLocationId,
          loadingCity: debouncedLoadingCity,
          loadingCountry: debouncedLoadingCountry,
          unloadingCity: debouncedUnloadingCity,
          unloadingCountry: debouncedUnloadingCountry,
          weight: debouncedWeight,
          countPallets: debouncedCountPallets,
          distance: debouncedDistance,
          cargoAmount: debouncedCargoAmount,
        },
        (response) => {
          setOptions(response);
          setLoading(false);
        },
        null,
        {
          onError: () => {
            setOptions([]);
            setLoading(false);
          },
        },
      );
    }
  }, [
    aiCarrierAutoSelectEnabled,
    loadingLocationId,
    unloadingLocationId,
    debouncedLoadingCity,
    debouncedLoadingCountry,
    debouncedUnloadingCity,
    debouncedUnloadingCountry,
    debouncedWeight,
    debouncedCountPallets,
    debouncedDistance,
    debouncedCargoAmount,
    aiCarrierAutoSelectionV3,
  ]);

  useEffect(() => {
    const filtered = allowedCarrierTypes.length
      ? options.filter(opt => allowedCarrierTypes.includes(opt.findCarrierType))
      : options;

    setFilteredOptions(
      multiSelect ? filtered : filtered.slice(0, 1),
    );
  }, [allowedCarrierTypes, options, multiSelect]);

  const isAllOptionsSelected = filteredOptions.length > 0 && filteredOptions.every(opt =>
    selectedCarriers.some(sel => (
      sel.id === opt.id &&
      isSameArray(sel.userIds, opt.userIds)
    )),
  );

  return (
    <>
      {aiCarrierAutoSelectEnabled && (
        <div
          id={rootId}
          className={[
            'w-full max-w-full flex items-center gap-3 px-3 py-2',
            'border border-solid border-content-secondary bg-base-tertiary shadow-[0_1px_4px_rgba(0,0,0,0.08)]',
            'text-content-primary',
            className || '',
          ].join(' ')}
        >
          <div className="shrink-0 relative flex items-center justify-center">
            <SvgRobot
              className={`inline-block w-[22px] h-[22px]`}
              width={22}
              height={22}
            />
          </div>

          <span className="text-sm md:text-base font-medium whitespace-nowrap">{label}</span>

          <div className="flex flex-wrap items-center gap-2">
            {(loading || (!loading && filteredOptions.length > 0)) && (
              <span className="inline-flex items-center">
                  <RingilButton
                    mode="text-only"
                    dataTestId={dataTestId}
                    icon={
                      loading ? (
                        <SvgLoading className="animate-spin animate-spin-slow" />
                      ) : (
                        isAllOptionsSelected ?
                        <SvgPlus className="text-content-secondary" />
                        : <SvgPlus />
                      )
                    }
                    label={
                      <span
                        className={
                          isAllOptionsSelected
                            ? "text-content-secondary"
                            : filteredOptions.length > 0
                              ? "text-brand-color"
                              : "text-content-secondary"
                        }
                      >
                        {tr('AiCarrierAutoSelect.bestLabel', 'Použít nejlepší')}
                      </span>
                    }
                    tooltip={
                      filteredOptions.length > 0
                        ? (
                          <div className="flex flex-col gap-1">
                            <span
                              className="text-content-quaternary font-dm-sans text-[14px] font-medium leading-[12px]">
                              {filteredOptions.map(opt => opt.name).join(', ')}
                            </span>
                            <span className="text-content-quaternary font-dm-sans text-[10px] font-normal leading-[12px]">
                              {tr('AiCarrierAutoSelect.bestTooltipDisclaimer', 'Based on AI analysis')}
                            </span>
                          </div>
                        )
                        : undefined
                    }
                    onClick={() => {
                      onSelect(filteredOptions.map(item => ({
                        id: item.id,
                        userIds: item.userIds,
                        carrierType: item.findCarrierType,
                        apiTypes: item.apiTypes,
                      })));
                    }}
                  />
                </span>
            )}
            {!loading && filteredOptions.length === 0 && (
              <span
                className="text-sm text-content-secondary italic">{tr('AiCarrierAutoSelect.foundZeroOptions', 'No options available')}</span>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default AiCarrierAutoSelection;
